import { PointsAccountingTokens } from '@/domain/DITokens'
import {
  IRelatedLicenseDto,
  IRelatedTransactionDto,
  ITransactionDto,
} from '@/domain/points/accounting/client/response/ITransactionDto'
import { IAuthorMapper } from '@/domain/points/accounting/mapper/contract/IAuthorMapper'
import { IClientMapper } from '@/domain/points/accounting/mapper/contract/IClientMapper'
import { ITransactionDetailsMapper } from '@/domain/points/accounting/mapper/contract/ITransactionDetailsMapper'
import { ITransactionMapper } from '@/domain/points/accounting/mapper/contract/ITransactionMapper'
import { Transaction } from '@/domain/points/accounting/model/Transaction'
import { inject, injectable } from 'inversify'
import { PayableService } from '@/domain/points/accounting/model/PayableService'
import { RelatedLicense } from '@/domain/points/accounting/model/RelatedLicense'
import { RelatedTransaction } from '@/domain/points/accounting/model/RelatedTransaction'

@injectable()
export class TransactionMapper implements ITransactionMapper {
  constructor(
    @inject(PointsAccountingTokens.mapper.ITransactionDetailsMapper)
    private readonly transactionDetailsMapper: ITransactionDetailsMapper,
    @inject(PointsAccountingTokens.mapper.IAuthorMapper)
    private readonly authorMapper: IAuthorMapper,
    @inject(PointsAccountingTokens.mapper.IClientMapper)
    private readonly clientMapper: IClientMapper,
  ) {
  }

  public toModel(dto: ITransactionDto): Transaction {
    return new Transaction(
      dto.$type,
      dto.id,
      new Date(dto.createdAt),
      this.transactionDetailsMapper.toModel(dto.details),
      this.clientMapper.toModel(dto.client),
      dto.updatedAt ? new Date(dto.updatedAt) : undefined,
      dto.author ? this.authorMapper.toModel(dto.author) : undefined,
      dto.billId ?? undefined,
      dto.service ? (dto.service as PayableService) : undefined,
      Array.isArray(dto.related_licenses)
        ? dto.related_licenses.map(license => this.toRelatedLicense(license))
        : [],
      Array.isArray(dto.related_transactions)
        ? dto.related_transactions
          .filter(related => !!related?.id)
          .map(related => this.toRelatedTransaction(related))
        : [],
    )
  }

  private toRelatedTransaction(dto: IRelatedTransactionDto): RelatedTransaction {
    return new RelatedTransaction(
      dto.id,
      dto.type,
      dto.relationship,
      dto.related_reason ?? undefined,
      dto.platform || undefined,
      dto.amount ?? undefined,
      this.toDate(dto.dateTime),
      dto.description || undefined,
      dto.user || undefined,
      dto.version || undefined,
    )
  }

  private toRelatedLicense(dto: IRelatedLicenseDto): RelatedLicense {
    return new RelatedLicense(
      dto.product,
      dto.number_of_seats,
      this.toDate(dto.expiration),
      this.toDate(dto.next_expiring_point_credit_date),
    )
  }

  private toDate(value?: string | null): Date | undefined {
    if (!value) {
      return undefined
    }

    const date = new Date(value.replace(/([+-]\d{2})(\d{2})$/, '$1:$2'))

    return isNaN(date.getTime()) ? undefined : date
  }
}
